/*
Enhanced LoanApplicationPage with Referrer Tracking using API functions
Handles referrer links and tracks conversions
*/

import React, { useState, useEffect } from 'react';
import PublicLoanApplication from './PublicLoanApplication';
import { 
    getShareableLinkDetails, 
    submitQuickLoanApplicationWithReferrer,
    extractReferrerFromURL 
} from '../../api';

const LoanApplicationPage = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
    const [referrerInfo, setReferrerInfo] = useState(null);
    const [referrerUuid, setReferrerUuid] = useState(null);
    const [isLoadingReferrer, setIsLoadingReferrer] = useState(false);

    // Cache key for sessionStorage
    const REFERRER_CACHE_KEY = 'referrer_info_cache';

    // Extract referrer UUID from URL parameters on component mount
    useEffect(() => {
        const refParam = extractReferrerFromURL();
        
        if (refParam) {
            setReferrerUuid(refParam);
            
            // Check if we have cached data for this referrer UUID
            const cachedData = getCachedReferrerInfo(refParam);
            
            if (cachedData) {
                setReferrerInfo(cachedData);
                console.log('Using cached referrer info:', cachedData.referrerDisplayName);
            } else {
                // Fetch fresh data if not cached
                fetchReferrerDetails(refParam);
            }
        }
    }, []);

    // Get cached referrer info from sessionStorage
    const getCachedReferrerInfo = (uuid) => {
        try {
            const cached = sessionStorage.getItem(REFERRER_CACHE_KEY);
            if (cached) {
                const parsedCache = JSON.parse(cached);
                
                // Check if cached data is for the same UUID and is still valid (within 1 hour)
                const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
                const isValid = 
                    parsedCache.uuid === uuid && 
                    parsedCache.timestamp && 
                    (Date.now() - parsedCache.timestamp) < oneHour;
                
                if (isValid) {
                    return parsedCache.data;
                }
            }
        } catch (error) {
            console.warn('Error reading cached referrer info:', error);
        }
        return null;
    };

    // Cache referrer info in sessionStorage
    const setCachedReferrerInfo = (uuid, data) => {
        try {
            const cacheData = {
                uuid: uuid,
                data: data,
                timestamp: Date.now()
            };
            sessionStorage.setItem(REFERRER_CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.warn('Error caching referrer info:', error);
        }
    };

    const fetchReferrerDetails = async (uuid) => {
        setIsLoadingReferrer(true);
        
        try {
            const response = await getShareableLinkDetails(uuid);
            
            if (response.error === 0) {
                setReferrerInfo(response.data);
                
                // Cache the successful response
                setCachedReferrerInfo(uuid, response.data);
                
                console.log('Referred by:', response.data.referrerDisplayName);
                console.log('Click count incremented for referrer link');
            } else {
                console.warn('Invalid referrer link:', response.message);
                setReferrerInfo(null);
            }
        } catch (error) {
            console.error('Error fetching referrer details:', error);
            setReferrerInfo(null);
        } finally {
            setIsLoadingReferrer(false);
        }
    };

    const handleSubmitApplication = async (applicationData) => {
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Submit application with referrer tracking using the new API
            const response = await submitQuickLoanApplicationWithReferrer(applicationData, referrerUuid);
            
            if (response.error === 0) {
                setSubmitStatus('success');
                console.log('Application submitted successfully:', response);
                
                if (referrerUuid) {
                    console.log('Application marked for referrer conversion tracking');
                }
            } else {
                setSubmitStatus('error');
                console.error('Application submission failed:', response.message);
            }
            
        } catch (error) {
            console.error('Error submitting application:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="loan-application-page">
            {submitStatus === 'success' && (
                <div className="success-overlay">
                    <div className="success-container">
                        <div className="success-icon">
                            <svg viewBox="0 0 24 24" className="checkmark">
                                <circle cx="12" cy="12" r="10" className="checkmark-circle"/>
                                <path d="M9 12l2 2 4-4" className="checkmark-path"/>
                            </svg>
                        </div>
                        
                        <div className="success-content">
                            <h1 className="success-title">Application Submitted Successfully!</h1>
                            
                            <div className="success-message">
                                <p className="primary-message">
                                    Thank you for choosing NFAL for your loan application. 
                                    We've received your application and our expert team is ready to assist you.
                                </p>
                                
                                <div className="next-steps">
                                    <h3>What happens next?</h3>
                                    <div className="steps-grid">
                                        <div className="step">
                                            <div className="step-number">1</div>
                                            <div className="step-content">
                                                <h4>Review Process</h4>
                                                <p>Our team will review your application within 24-48 hours</p>
                                            </div>
                                        </div>
                                        
                                        <div className="step">
                                            <div className="step-number">2</div>
                                            <div className="step-content">
                                                <h4>Personal Contact</h4>
                                                <p>We'll contact you to discuss your requirements and next steps</p>
                                            </div>
                                        </div>
                                        
                                        <div className="step">
                                            <div className="step-number">3</div>
                                            <div className="step-content">
                                                <h4>Application Progress</h4>
                                                <p>You'll receive regular updates on your application status</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {referrerInfo && (
                                    <div className="referrer-acknowledgment">
                                        <div className="referrer-badge">
                                            <svg viewBox="0 0 24 24" className="referrer-icon">
                                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                                            </svg>
                                            <div className="referrer-info">
                                                <span className="referrer-label">Referred by</span>
                                                <span className="referrer-name">{referrerInfo.referrerDisplayName}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                
                                {/*<div className="confirmation-notice">
                                    <div className="notice-icon">
                                        <svg viewBox="0 0 24 24">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                                        </svg>
                                    </div>
                                    <div className="notice-content">
                                        <p>You will receive a confirmation email shortly with your application reference number and contact details for any queries.</p>
                                    </div>
                                </div>*/}
                                
                                <div className="contact-support">
                                    <p>Need immediate assistance? Contact our support team:</p>
                                    <div className="contact-methods">
                                        <a href="tel:1300123456" className="contact-method">
                                            <svg viewBox="0 0 24 24">
                                                <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                                            </svg>
                                            1300 844 970
                                        </a>
                                        <a href="mailto:support@nfal.com.au" className="contact-method">
                                            <svg viewBox="0 0 24 24">
                                                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                            </svg>
                                            support@nfal.com.au
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {submitStatus === 'error' && (
                <div className="error-overlay">
                    <div className="error-container">
                        <div className="error-icon">
                            <svg viewBox="0 0 24 24" className="error-mark">
                                <circle cx="12" cy="12" r="10" className="error-circle"/>
                                <path d="M15 9l-6 6M9 9l6 6" className="error-path"/>
                            </svg>
                        </div>
                        
                        <div className="error-content">
                            <h2 className="error-title">Submission Error</h2>
                            <p className="error-message">
                                We're sorry, but there was an error submitting your application. 
                                Please try again or contact our support team for assistance.
                            </p>
                            
                            <div className="error-actions">
                                <button 
                                    onClick={() => setSubmitStatus(null)}
                                    className="btn-retry"
                                >
                                    <svg viewBox="0 0 24 24">
                                        <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                                    </svg>
                                    Try Again
                                </button>
                                
                                <a href="mailto:support@nfal.com.au" className="btn-contact">
                                    <svg viewBox="0 0 24 24">
                                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.89 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                    </svg>
                                    Contact Support
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {submitStatus !== 'success' && (
                <PublicLoanApplication 
                    onSubmit={handleSubmitApplication}
                    isSubmitting={isSubmitting}
                    referrerInfo={referrerInfo}
                />
            )}
        </div>
    );
};

export default LoanApplicationPage;